// Tickets list — every Jira ticket the agent has worked on. Pagination +
// search + filtering are server-side: the tickets endpoint takes
// limit/offset/query/latestStatus/hasPr and returns { items, total, limit,
// offset }. We fetch one page (25) at a time, re-fetching on page / search /
// filter change.

import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import Shell from "./Shell";
import AssignPrModal from "./AssignPrModal";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  RefreshButton,
  StatusBadge,
  apiErrorMessage,
  formatMoney,
  isLive,
  outcomeColor,
  relativeTime,
  rowStatusClass
} from "./shared";

const FILTERS = [
  ["all", "All"],
  ["in_flight", "In flight"],
  ["failed", "Failed"],
  ["deployed", "Ready to deploy"],
  ["open_pr", "Has PR"]
];

const PAGE_SIZE = 25;

// Filter tab -> server params. in_flight is the "active" meta-status
// (latest run not COMPLETED / FAILED / QUEUED); open_pr filters by hasPr.
function paramsForFilter(filter) {
  switch (filter) {
    case "in_flight":
      return { latestStatus: "active" };
    case "failed":
      return { latestStatus: "FAILED" };
    case "deployed":
      return { latestStatus: "COMPLETED" };
    case "open_pr":
      return { hasPr: "true" };
    default:
      return {};
  }
}

export default function TicketsPage() {
  const history = useHistory();
  const [filter, setFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(0);
  const [assignOpen, setAssignOpen] = useState(false);

  // Debounce the search box so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // A new filter tab / project / search term resets to the first page.
  useEffect(() => {
    setPage(0);
  }, [filter, projectFilter, debouncedQuery]);

  const fetchTickets = useCallback(
    () =>
      api
        .bssTickets({
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          query: debouncedQuery || undefined,
          ...paramsForFilter(filter),
          ...(projectFilter ? { project: projectFilter } : {})
        })
        .then((res) => res.data),
    [page, debouncedQuery, filter, projectFilter]
  );
  const { data, error, loading, stale, refresh } = useBssPoll(fetchTickets, {
    deps: [page, debouncedQuery, filter, projectFilter]
  });
  const tickets = (data && data.items) || [];
  const total = (data && data.total) || 0;
  // Distinct Jira projects, server-computed over ALL ticket groups (before
  // filtering) so a selected project never hides the other options.
  const projects = (data && data.projects) || [];
  const offset = page * PAGE_SIZE;

  const openTicket = (key) =>
    history.push(`/bss/tickets/${encodeURIComponent(key)}`);

  // Toolbar is rendered UNCONDITIONALLY so the search input + filter pills
  // stay mounted across every refetch. Previously the whole `body` was
  // swapped to <LoadingState /> on each loading=true tick, which destroyed
  // the search input and made it visibly flash on every keystroke.
  const toolbar = (
    <div className="bss-toolbar">
      <div className="bss-filter-tabs">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`bss-filter-tab ${
              filter === key ? "is-active" : ""
            }`.trim()}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <select
        className="bss-input"
        style={{ width: "auto", marginLeft: 8 }}
        value={projectFilter}
        onChange={(e) => setProjectFilter(e.target.value)}
        title="Filter by Jira project"
      >
        <option value="">All projects</option>
        {projects.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
      <div className="bss-search-wrap" style={{ flex: 1, marginLeft: 8 }}>
        <Icon.Search size={14} className="bss-icon" />
        <input
          className="bss-input"
          placeholder="Filter by ticket key or summary…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
    </div>
  );

  let body;
  // Only show LoadingState / ErrorState on the FIRST load (no data yet).
  // For refetches with data already in hand we keep the previous results
  // visible (stale-while-revalidate) so the page doesn't flash.
  if (loading && !data) body = <LoadingState label="Loading tickets…" />;
  else if (error && !data)
    body = <ErrorState message={apiErrorMessage(error)} onRetry={refresh} />;
  else
    body = (
      <>
        <div className="bss-card" style={{ overflow: "hidden" }}>
          <table className="bss-table">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Ticket</th>
                <th>Summary</th>
                <th style={{ width: 150 }}>Latest run</th>
                <th style={{ width: 80, textAlign: "right" }}>Runs</th>
                <th style={{ width: 100, textAlign: "right" }}>Spend</th>
                <th style={{ width: 90 }}>PR</th>
                <th style={{ width: 160 }}>Last deploy</th>
                <th style={{ width: 36 }} aria-label="open" />
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => {
                const r = t.latest || {};
                const live = isLive(r.status);
                return (
                  <tr
                    key={t.key}
                    className={rowStatusClass(r.status, live)}
                    onClick={() => openTicket(t.key)}
                  >
                    <td>
                      <div className="bss-table-cell-key">
                        {t.mode === "test_plan" ? (
                          <Icon.FileText
                            size={14}
                            style={{ color: "var(--bss-info)" }}
                          />
                        ) : (
                          <Icon.Ticket
                            size={14}
                            style={{ color: "var(--bss-boom-pink)" }}
                          />
                        )}
                        <span>{t.key}</span>
                      </div>
                    </td>
                    <td>
                      <div className="bss-table-cell-summary">
                        <span>{t.summary || "—"}</span>
                        {r.status === "FAILED" && r.errorPreview && (
                          <span className="bss-preview-err">
                            <Icon.AlertTriangle size={11} />
                            {r.errorPreview}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 3
                        }}
                      >
                        <StatusBadge status={r.status} pulsing={live} />
                        <span
                          style={{
                            fontSize: 11,
                            color: "var(--bss-fg-subtle)"
                          }}
                        >
                          {relativeTime(r.startedAt)}
                        </span>
                      </div>
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontFamily: "var(--bss-font-mono)",
                        fontSize: 12
                      }}
                    >
                      {t.runCount}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontFamily: "var(--bss-font-mono)",
                        fontSize: 12
                      }}
                    >
                      {formatMoney(t.totalSpendUsd)}
                    </td>
                    <td>
                      {t.lastPr ? (
                        <a
                          href={t.lastPr.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bss-badge bss-badge--gray"
                          style={{
                            color: "var(--bss-info)",
                            textDecoration: "none"
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Icon.GitPullRequest size={11} />#{t.lastPr.number}
                        </a>
                      ) : (
                        <span
                          style={{
                            fontSize: 12,
                            color: "var(--bss-fg-subtle)"
                          }}
                        >
                          —
                        </span>
                      )}
                    </td>
                    <td>
                      {t.lastDeploy ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12
                          }}
                        >
                          <span
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: 999,
                              background: outcomeColor(t.lastDeploy.status)
                            }}
                          />
                          <span
                            style={{
                              color: "var(--bss-fg)",
                              fontFamily: "var(--bss-font-mono)",
                              fontSize: 11.5
                            }}
                          >
                            {t.lastDeploy.env}
                          </span>
                          <span
                            style={{
                              color: "var(--bss-fg-subtle)",
                              fontSize: 11.5
                            }}
                          >
                            {relativeTime(t.lastDeploy.startedAt)}
                          </span>
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: 12,
                            color: "var(--bss-fg-subtle)"
                          }}
                        >
                          Never deployed
                        </span>
                      )}
                    </td>
                    <td>
                      <Icon.ChevronRight
                        size={14}
                        style={{ color: "var(--bss-fg-subtle)" }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {tickets.length === 0 && (
            <EmptyState
              title="Nothing matches."
              hint="Try clearing the filter or your search."
            />
          )}
        </div>

        <div className="bss-list-foot">
          <div>
            {total === 0
              ? "showing 0 of 0"
              : `showing ${offset + 1}–${offset + tickets.length} of ${total}`}
            {stale && (
              <span
                style={{ color: "var(--bss-warning, #b7791f)", marginLeft: 8 }}
                title={apiErrorMessage(error, "Last refresh failed")}
              >
                · reconnecting — showing last known data
              </span>
            )}
          </div>
          <div className="bss-pager">
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              disabled={offset + tickets.length >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </>
    );

  return (
    <Shell>
      <div className="bss-page-hd">
        <div>
          <h1>Tickets</h1>
          <div className="bss-page-hd-sub">
            Every Jira ticket the agent has touched. Click a ticket to see its
            full run history and trigger deploys.
          </div>
        </div>
        <div className="bss-page-hd-right">
          <button
            type="button"
            className="bss-btn bss-btn--ghost"
            onClick={() => setAssignOpen(true)}
            title="File a PR under a ticket key (existing ticket or a new group)"
          >
            <Icon.Plus size={14} /> Assign PR…
          </button>
          <RefreshButton onClick={refresh} loading={loading} />
        </div>
      </div>
      {toolbar}
      {body}
      <AssignPrModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onAssigned={refresh}
      />
    </Shell>
  );
}
